import React, { Suspense } from "react";
import { Redirect, Switch } from "react-router-dom";
import { LayoutSplashScreen, ContentRoute } from "../_metronic/layout";
import { useSelector, shallowEqual } from "react-redux";
import {
  hasPermission,
  getDefaultRouteForUser,
} from "./utils/permissionUtils";
import {
  UserManagement,
  UserManagementDetail,
  AvtarManagement,
  TopUserManagement,
} from "./modules/user_management";
import { Category, Subcategory } from "./modules/services";
import { DashboardPage } from "./pages/DashboardPage";
import {
  AdminManagement,
  AdminManagementDetail,
} from "./modules/admin_management";
import { EmailManagement } from "./modules/email_management";
import { ProfileSettings } from "./modules/profile_settings";
import { ThirdPartyServices } from "./modules/third_party_services";
import AddTemplate from "./modules/email_management/pages/AddTemplate";
import { CmsPages } from "./modules/cms_pages";
import { CmsPageDetail } from "./modules/cms_pages";
import AddNotificationTemplate from "./modules/notification_management/pages/AddNotification";
import { NotificationManagement } from "./modules/notification_management/pages/notificationManagement";
import AddCategory from "./modules/services/pages/AddCategory";
import AddSubcategory from "./modules/services/pages/AddSubcategory";
import { BetManagement } from "./modules/bet_management/pages/betManagement";
import { Payment } from "./modules/payment/pages/payment";
import EventTable from "./modules/dashboards/EventTable";
import ReportsAndAnaytics from "./modules/reports_and_analytics/pages/ReportsAndAnaytics";
import { ticketSupportManagement } from "./modules/ticketSupportManagement/pages/ticketSupportManagement";
import { TeamManagement } from "./modules/team_management/pages/teamManagement";
import BetUserResultManagement from "./modules/bet_management/pages/bet_management_detail/betUserResultMangement";
import { FaqPages } from "./modules/faq_management";
import { FaqPageDetail } from "./modules/faq_management";
import { PromoCodeManagement } from "./modules/promo_code_management";
import { VirtualTokenManagement } from "./modules/virtual_token";
import { TokenTransactions } from "./modules/token_transactions";
import TokenTransactionDetail from "./modules/token_transactions/pages/token_tnx_detail/tokenTnxDetail";
import SystemSettingsForm from "./modules/system_settings/pages/systemSettingform";
import CommissionHistoryDetail from "./modules/commission_history/pages/commission_history_detail/commissionHistoryDetail";
import { CommissionHistory } from "./modules/commission_history";
import { CurrencyManagement } from "./modules/currency/pages/currencyManagement";
import { WithdrawalRequest } from "./modules/withdrawal_management";
import { ReferralBonusCoins } from "./modules/referral_bonus_coins";

export default function BasePage() {
  const { user } = useSelector(({ auth }) => ({ user: auth.user }), shallowEqual);
  const role = user?.role;
  const allowedMenus = Array.isArray(user?.allowedMenus) ? user.allowedMenus : [];

  const hasAccess = React.useCallback(
    (permissionKey) => hasPermission(allowedMenus, permissionKey, role),
    [role, allowedMenus]
  );

  const defaultRoute = React.useMemo(
    () => getDefaultRouteForUser(allowedMenus, role),
    [role, allowedMenus]
  );

  const PermissionRoute = ({ permissionKey, ...rest }) => {
    if (!hasAccess(permissionKey)) {
      return <Redirect to={defaultRoute} />;
    }
    return <ContentRoute {...rest} />;
  };

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <Switch>
        {<Redirect exact from="/" to={defaultRoute} />}
        <PermissionRoute
          exact
          path="/dashboard"
          component={DashboardPage}
          permissionKey="dashboard"
        />
        <PermissionRoute
          exact
          path="/event-table"
          component={EventTable}
          permissionKey="events"
        />
        <PermissionRoute path="/profile-settings" component={ProfileSettings} />
        <PermissionRoute
          exact
          path="/user-management"
          component={UserManagement}
          permissionKey="users.list"
        />
        <PermissionRoute
          path="/user-management/user-management-detail"
          component={UserManagementDetail}
          permissionKey="users.list"
        />
        <PermissionRoute
          path="/top-user-management"
          component={TopUserManagement}
          permissionKey="users.top"
        />

        {/* <ContentRoute path="/avtar-management" component={AvtarManagement} /> */}

        <PermissionRoute
          exact
          path="/admin-management"
          component={AdminManagement}
          permissionKey="adminManagement"
        />
        <PermissionRoute
          path="/admin-management/admin-management-detail"
          component={AdminManagementDetail}
          permissionKey="adminManagement"
        />
        <PermissionRoute
          exact
          path="/email-management"
          component={EmailManagement}
          permissionKey="email"
        />

        <PermissionRoute
          exact
          path="/virtual-token"
          component={VirtualTokenManagement}
          permissionKey="virtualToken"
        />

        <PermissionRoute
          exact
          path="/email-management/add-template"
          component={AddTemplate}
          permissionKey="email"
        />
        <PermissionRoute
          exact
          path="/email-management/edit-template/:id"
          component={AddTemplate}
          permissionKey="email"
        />
        <PermissionRoute
          exact
          path="/services/category"
          component={Category}
          permissionKey="services.category"
        />
        <PermissionRoute
          exact
          path="/services/category/add-category"
          component={AddCategory}
          permissionKey="services.category"
        />
        <PermissionRoute
          exact
          path="/services/subcategory"
          component={Subcategory}
          permissionKey="services.subcategory"
        />
        <PermissionRoute
          exact
          path="/services/subcategory/add-sub-category"
          component={AddSubcategory}
          permissionKey="services.subcategory"
        />
        <PermissionRoute
          exact
          path="/cms-pages"
          component={CmsPages}
          permissionKey="cms"
        />
        <PermissionRoute
          path="/cms-pages/cms-page-detail"
          component={CmsPageDetail}
          permissionKey="cms"
        />
        <PermissionRoute
          exact
          path="/faq-pages"
          component={FaqPages}
          permissionKey="faq"
        />
        <PermissionRoute
          path="/faq-pages/faq-page-detail"
          component={FaqPageDetail}
          permissionKey="faq"
        />
        <PermissionRoute
          exact
          path="/promo-codes"
          component={PromoCodeManagement}
          permissionKey="promoCodes"
        />
        <PermissionRoute
          path="/cms-pages/cms-page-detail"
          component={CmsPageDetail}
          permissionKey="cms"
        />
        <PermissionRoute
          exact
          path="/bet-management"
          component={BetManagement}
          permissionKey="challenges"
        />

        <PermissionRoute
          exact
          path="/bet-management/bet-result-details/:id"
          component={BetUserResultManagement}
          permissionKey="challenges"
        />

        {/* <ContentRoute exact path="/payment" component={Payment} /> */}

        <PermissionRoute
          exact
          path="/currencies"
          component={CurrencyManagement}
          permissionKey="currencies"
        />

        <PermissionRoute
          exact
          path="/token-transaction"
          component={TokenTransactions}
          permissionKey="tokens"
        />

        <PermissionRoute
          exact
          path="/withdrawal-request"
          component={WithdrawalRequest}
          permissionKey="withdrawals"
        />

        <PermissionRoute
          exact
          path="/referral-bonus-coins"
          component={ReferralBonusCoins}
          permissionKey="users.referral"
        />

        <PermissionRoute
          exact
          path="/token-transaction/token-transaction-details/:id"
          component={TokenTransactionDetail}
          permissionKey="tokens"
        />

        <PermissionRoute
          exact
          path="/commission-history"
          component={CommissionHistory}
          permissionKey="commission"
        />

        <PermissionRoute
          exact
          path="/commission-history/commission-history-details/:id"
          component={CommissionHistoryDetail}
          permissionKey="commission"
        />

        <PermissionRoute
          exact
          path="/reports-and-analytics"
          component={ReportsAndAnaytics}
          permissionKey="reports"
        />
        {/* <ContentRoute
          exact
          path="/notification-management"
          component={NotificationManagement}
        />
        <ContentRoute
          exact
          path="/notification-management/add-template"
          component={AddNotificationTemplate}
        />
        <ContentRoute
          exact
          path="/notification-management/edit-template/:id"
          component={AddNotificationTemplate}
        /> */}

        <PermissionRoute
          exact
          path="/ticket-support-management"
          component={ticketSupportManagement}
          permissionKey="tickets"
        />

        <PermissionRoute
          exact
          path="/team-management"
          component={TeamManagement}
          permissionKey="team"
        />

        <PermissionRoute
          exact
          path="/system-settings"
          component={SystemSettingsForm}
          permissionKey="system"
        />

        {/* <ContentRoute
          exact
          path="/notification-management"
          component={NotificationManagement}
        />
        <ContentRoute
          exact
          path="/notification-management/add-template"
          component={AddNotificationTemplate}
        />
        <ContentRoute
          exact
          path="/notification-management/edit-template/:id"
          component={AddNotificationTemplate}
        /> */}

        {/* <ContentRoute
          path="/third-party-services"
          component={ThirdPartyServices}
        />
        <ContentRoute exact path="/cms-pages" component={CmsPages} />
        <ContentRoute
          path="/cms-pages/cms-page-detail"
          component={CmsPageDetail}
        />
        <ContentRoute
          path="/cms-pages/cms-page-detail"
          component={CmsPageDetail}
        />
        <ContentRoute path="/default-password" component={DefaultPassword} />
        <ContentRoute exact path="/faq-management" component={FaqManagement} /> */}
        {/* <ContentRoute exact path="/faq-management/add-faq" component={AddFAQ} /> */}
        {/* <ContentRoute
          exact
          path="/faq-management/edit-faq"
          component={UpdateFAQ}
        /> */}
        {/* 
        <ContentRoute
          exact
          path="/master-management"
          component={MasterManagement}
        />
        */}
        <Redirect to="error/error-v1" />
      </Switch>
    </Suspense>
  );
}
